import Link from "next/link";
import { Calendar, Clock, Eye, Heart } from "lucide-react";
import type { Post } from "@/lib/blog/store";
import { getCategories } from "@/lib/blog/store";

export default function PostCard({
  post,
  variant = "default",
}: {
  post: Post;
  variant?: "default" | "featured" | "compact";
}) {
  const cat = getCategories().find((c) => c.id === post.categoryId);
  const date = new Date(post.publishedAt).toLocaleDateString("fa-IR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  if (variant === "featured") {
    return (
      <Link
        href={`/blog/${post.slug}`}
        className="group relative block overflow-hidden rounded-3xl border border-border bg-card transition-all hover:border-primary-main hover:shadow-2xl hover:shadow-primary-main/10"
      >
        <div className="grid lg:grid-cols-2">
          <div
            className="relative aspect-[16/10] lg:aspect-auto"
            style={{ background: post.cover }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-[11px] font-black text-grayScale-900 backdrop-blur">
              ⭐ مقاله ویژه
            </span>
          </div>
          <div className="flex flex-col justify-center gap-4 p-6 lg:p-10">
            {cat && <span className={`text-xs font-black ${cat.color}`}>{cat.name}</span>}
            <h2 className="text-2xl font-black leading-tight text-foreground transition-colors group-hover:text-primary-main lg:text-3xl">
              {post.title}
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">{post.excerpt}</p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-main/15 text-[10px] font-black text-primary-main">
                  {post.author.avatar}
                </div>
                <span className="font-bold text-foreground">{post.author.name}</span>
              </div>
              <span className="flex items-center gap-1">
                <Calendar size={11} />
                {date}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={11} />
                {post.readingMinutes} دقیقه
              </span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        href={`/blog/${post.slug}`}
        className="group flex gap-3 rounded-xl border border-border bg-card p-3 transition-all hover:border-primary-main"
      >
        <div className="h-16 w-16 flex-shrink-0 rounded-lg" style={{ background: post.cover }} />
        <div className="min-w-0 flex-1">
          {cat && <span className={`text-[10px] font-bold ${cat.color}`}>{cat.name}</span>}
          <h4 className="line-clamp-2 text-[13px] font-bold text-foreground transition-colors group-hover:text-primary-main">
            {post.title}
          </h4>
          <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-muted-foreground">
            <Clock size={10} />
            {post.readingMinutes} دقیقه
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:border-primary-main hover:shadow-xl hover:shadow-primary-main/10"
    >
      <div className="relative aspect-[16/10]" style={{ background: post.cover }}>
        {cat && (
          <span className="absolute right-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-black text-grayScale-900 backdrop-blur">
            {cat.name}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3 className="line-clamp-2 text-base font-black leading-snug text-foreground transition-colors group-hover:text-primary-main">
          {post.title}
        </h3>
        <p className="line-clamp-2 flex-1 text-[13px] leading-relaxed text-muted-foreground">
          {post.excerpt}
        </p>
        <div className="flex items-center justify-between pt-2 text-[11px] text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-main/15 text-[9px] font-black text-primary-main">
              {post.author.avatar}
            </div>
            <span className="font-bold text-foreground">{post.author.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Eye size={10} />
              {post.views.toLocaleString("fa-IR")}
            </span>
            <span className="flex items-center gap-1">
              <Heart size={10} />
              {post.likes}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
